import React, { useCallback } from 'react';

import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	type AnalyticsEventPayload,
} from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { MediaInsertTabProps } from '@atlaskit/editor-plugin-media-insert';
import { ImageFromAIGeneration } from '@atlassian/ai-image-generation/image-from-ai-generation';

import type { AIImageGenerationPlugin } from '../aiImageGenerationPluginType';

const DEFAULT_ASPECT_RATIO = '16:9';
const PHOTO_AUTO_IMAGE_STYLE = 'Photo (Auto)';

/**
 * Adapter that renders `<ImageFromAIGeneration>` (a private
 * `@atlassian/ai-image-generation` component) inside the public media insert
 * picker as a registered tab.
 *
 * Lives in this private package (which already depends on the private
 * AI image-generation package) so that `@atlaskit/editor-plugin-media-insert`
 * never has to import any `@atlassian/*` package.
 *
 * Wired in via `api.mediaInsert.actions.registerInsertTab(...)` from
 * `aiImageGenerationPlugin`'s setup.
 *
 * On submit, instead of generating an image directly, this delegates to the
 * malleable-ui flow via `api.malleableUi.commands.startRemixThinking` with the
 * user's prompt pre-loaded. The remix modal detects the pre-supplied prompt
 * and skips its option-picker UI, going straight to processing.
 */
export function MediaInsertImageGenerationTab({
	closeMediaInsertPicker,
	dispatchAnalyticsEvent,
	mediaProvider,
	api,
}: MediaInsertTabProps & { api: ExtractInjectionAPI<AIImageGenerationPlugin> }): React.JSX.Element {
	const handleSubmit = useCallback(
		(prompt: string, aspectRatio: string | null, imageStyle: string | null) => {
			closeMediaInsertPicker();

			if (dispatchAnalyticsEvent) {
				const payload: AnalyticsEventPayload = {
					action: ACTION.SUBMITTED,
					actionSubject: ACTION_SUBJECT.PICKER,
					actionSubjectId: ACTION_SUBJECT_ID.PICKER_MEDIA,
					eventType: EVENT_TYPE.UI,
					attributes: {
						aiFeatureName: 'imageGenerationMediaInsert',
						aspectRatio: aspectRatio?.trim() || DEFAULT_ASPECT_RATIO,
						imageStyle: imageStyle ?? PHOTO_AUTO_IMAGE_STYLE,
					},
				};
				dispatchAnalyticsEvent(payload);
			}

			const remixModalMetadata = {
				interactionSource: 'remixModalFromMediaInserts' as const,
				aiFeatureName: 'imageGenerationMediaInsert',
				userPrompt: prompt,
				aspectRatio,
				imageStyle,
				dynamicUiType: 'image' as const,
				remixSource: 'mediaInsert',
				onCancel: () => {
					const cmd = api?.mediaInsert?.commands.showMediaInsertPopup();

					if (cmd) {
						api?.core.actions.execute(cmd);
					}
				},
			};
			const cmd = api?.malleableUi?.commands.startRemixThinking({
				remixModalMetadata,
			});
			if (!cmd) {
				return;
			}
			api?.core.actions.execute(cmd);
		},
		[api, closeMediaInsertPicker, dispatchAnalyticsEvent],
	);

	return (
		<ImageFromAIGeneration
			mediaUploadCollection={mediaProvider.uploadParams?.collection}
			closePopup={closeMediaInsertPicker}
			onSubmit={handleSubmit}
		/>
	);
}
